import { defineStore } from 'pinia'
import { ref } from 'vue'
import { setStorageProvider, getStorage } from '../storage'
import { setModelBinaryFetcher } from '../utils/modelBinary'
import { setSkillCloudTenant } from '@/ai/agent/skillCloud'
import * as api from './api'
import { CloudStorageProvider } from './cloudStorageProvider'

export interface CloudProjectRef {
  publicId: string
  name: string
  baseVersionSeq: number
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error' | 'conflict' | 'offline'

/** 平台会话：登录态 / 当前租户 / 当前云项目 / 云保存状态（docs/10：登录墙前置，无本地模式） */
export const usePlatformStore = defineStore('platform', () => {
  const ready = ref(false)
  const loggedIn = ref(false)
  const account = ref<string | null>(null)
  const userId = ref<number | null>(null)
  const tenant = ref<{ publicId: string; name: string } | null>(null)
  const currentProject = ref<CloudProjectRef | null>(null)
  const saveState = ref<SaveState>('idle')
  const saveMessage = ref('')
  /** 最近一次保存是否手动触发（保存成功提示只对手动保存短暂显示，autosave 静默） */
  const saveManual = ref(false)
  const conflictServerSeq = ref<number | null>(null)

  /** 登录态下把存储注入点切到云端实现，并给模型二进制桥 / Skill 云端注入会话（docs/09 §6.2 / docs/10 §2.10） */
  function switchToCloudProvider(): void {
    if (!tenant.value) return
    setStorageProvider(new CloudStorageProvider(tenant.value.publicId))
    setSkillCloudTenant(tenant.value.publicId)
    setModelBinaryFetcher(async (sha, name) => {
      try {
        return await getStorage().assets.getBinary({ sha256: sha, name: name ?? '', contentType: '', sizeBytes: 0 })
      } catch {
        return null
      }
    })
  }

  /** 启动时用 refresh cookie 恢复会话；顺带回填 userId/account——否则软编辑锁的"是否是我自己"判断、
   *  顶栏账号展示全都拿不到值（login()/register() 才会设，但日常大多数会话是走这条 cookie 恢复路径） */
  async function restoreSession(): Promise<void> {
    const ok = await api.tryRefresh()
    if (ok) {
      loggedIn.value = true
      const user = api.getRefreshedUser()
      if (user) {
        userId.value = user.id
        account.value = user.account
      }
      await loadTenant()
    }
    ready.value = true
  }

  async function loadTenant(): Promise<void> {
    try {
      const tenants = await api.apiTenants()
      if (tenants.length) {
        tenant.value = { publicId: tenants[0].publicId, name: tenants[0].name }
        switchToCloudProvider()
        return
      }
    } catch {
      /* 会话失效走下面置空 */
    }
    loggedIn.value = false
    tenant.value = null
  }

  async function login(accountInput: string, password: string): Promise<void> {
    const r = await api.apiLogin(accountInput, password)
    api.setAccessToken(r.accessToken)
    loggedIn.value = true
    account.value = r.user.account
    userId.value = r.user.id
    await loadTenant()
  }

  async function register(accountInput: string, password: string, displayName?: string): Promise<void> {
    const r = await api.apiRegister(accountInput, password, displayName)
    api.setAccessToken(r.accessToken)
    loggedIn.value = true
    account.value = r.user.account
    userId.value = r.user.id
    await loadTenant()
  }

  async function logout(): Promise<void> {
    await api.apiLogout()
    api.setAccessToken(null)
    loggedIn.value = false
    account.value = null
    userId.value = null
    tenant.value = null
    currentProject.value = null
    saveState.value = 'idle'
    saveMessage.value = ''
    setSkillCloudTenant(null)
  }

  function openProject(p: CloudProjectRef): void {
    currentProject.value = p
  }

  function setSaveState(state: SaveState, message = '', manual = false): void {
    saveState.value = state
    saveMessage.value = message
    saveManual.value = manual
  }

  return {
    ready,
    loggedIn,
    account,
    userId,
    tenant,
    currentProject,
    saveState,
    saveMessage,
    saveManual,
    conflictServerSeq,
    restoreSession,
    loadTenant,
    login,
    register,
    logout,
    openProject,
    setSaveState,
  }
})
